import React from 'react';
import NearbyMap, { NearbySpot } from '@cubemeet/ui/components/travel/NearbyMap';
import { buildLatLng } from '../utils/coordinateHelpers';
import { parseTimeToMinutes } from '../timeUtils';

export interface RouteMapSheetProps {
  visible: boolean;
  selectedDay: any;
  routeSpots: NearbySpot[] | null;
  routeSpotsLoading: boolean;
  sheetHeight: number;
  sheetBottomPadding: number;
  itineraryName?: string;
  onSelectSpot: (spotId: string) => void;
  onSelectListItem: (index: number) => void;
}

/**
 * 路线地图抽屉组件
 * 显示当日行程的地图视图和景点列表
 */
export const RouteMapSheet: React.FC<RouteMapSheetProps> = ({
  visible,
  selectedDay,
  routeSpots,
  routeSpotsLoading,
  sheetHeight,
  sheetBottomPadding,
  itineraryName = '',
  onSelectSpot,
  onSelectListItem
}) => {
  if (!visible) return null;

  // 构建 spots 数据
  const spots = React.useMemo(() => {
    // 如果已经有 enriched routeSpots，直接使用
    if (routeSpots && Array.isArray(routeSpots)) {
      return routeSpots;
    }

    // 否则从 selectedDay.items 构建初始 spots
    const items = selectedDay?.items || [];
    
    // 按时间排序
    const sortedItems = items.slice().sort((a: any, b: any) => {
      const timeA = parseTimeToMinutes(a?.time);
      const timeB = parseTimeToMinutes(b?.time);
      return timeA - timeB;
    });

    // 构建 NearbySpot 数组
    const initialSpots: NearbySpot[] = sortedItems
      .map((item: any, idx: number) => {
        const src = item?.payload || item?.custom_payload || item?.inline_payload || item;
        const latlng = buildLatLng(src) || buildLatLng(item) || null;
        
        if (!latlng) return null;

        const name = item?.title || src?.title || src?.name || `景点 ${idx + 1}`;
        const imageUrl = src?.imageUrl || src?.image || src?.cover_image || src?.thumbnail || null;
        const address = src?.address || src?.formatted_address || null;

        return {
          id: String(item?.id || idx),
          name,
          lat: latlng[0],
          lng: latlng[1],
          imageUrl,
          address
        } as NearbySpot;
      })
      .filter(Boolean) as NearbySpot[];

    return initialSpots;
  }, [routeSpots, selectedDay]);

  // 计算地图中心点
  const center: [number, number] = spots.length > 0 
    ? [spots[0].lat, spots[0].lng] 
    : [0, 0];

  // 地图高度：展开状态（>80%）显示更大地图
  const mapHeight = sheetHeight > 80 ? 360 : 320;

  return (
    <div style={{ padding: 16, paddingBottom: sheetBottomPadding + 32 }}>
      {/* 地图区域 */}
      <div 
        style={{ 
          width: '100%', 
          height: mapHeight, 
          borderRadius: 12, 
          overflow: 'hidden', 
          marginBottom: 8 
        }}
      >
        <NearbyMap
          center={center}
          spots={spots}
          primaryLabel={itineraryName}
          showTotal={true}
          showNumberLabels={true}
          onSelectSpot={onSelectSpot}
          hideFullscreenButton={true}
          requireDoubleClick={true}
        />
      </div>

      {/* 景点列表 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {spots.length > 0 ? (
          spots.map((spot, index) => (
            <SpotListItem
              key={spot.id}
              spot={spot}
              index={index}
            />
          ))
        ) : (
          <EmptyState loading={routeSpotsLoading} />
        )}
      </div>
    </div>
  );
};

/**
 * 景点列表项组件
 */
interface SpotListItemProps {
  spot: NearbySpot;
  index: number;
}

const SpotListItem: React.FC<SpotListItemProps> = ({ spot, index }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderRadius: 8,
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
      }}
    >
      {/* 序号徽章 */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: '#2563EB',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 18,
          flexShrink: 0
        }}
      >
        {index + 1}
      </div>

      {/* 景点信息 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {spot.name}
        </div>
        {spot.address && (
          <div
            style={{
              fontSize: 13,
              color: '#6B7280',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {spot.address}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 空状态组件
 */
interface EmptyStateProps {
  loading: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({ loading }) => {
  return (
    <div
      style={{
        padding: 24,
        textAlign: 'center',
        color: '#9CA3AF',
        fontSize: 14
      }}
    >
      {loading ? '加载中...' : '今日暂无可定位的景点'}
    </div>
  );
};

export default RouteMapSheet;
